import React, { Suspense, useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// Import i18n configuration
import './i18n';

// Import contexts
import { CartProvider } from './contexts/CartContext';

// Import components
import Header from './components/layout/Header';
import Hero from './components/sections/Hero';
import ImagePreviewGallery from './components/sections/ImagePreviewGallery';
import HowWeWork from './components/sections/HowWeWork';
import Pricing from './components/sections/Pricing';
import ContactForm from './components/sections/ContactForm';
import FullGallery from './components/sections/FullGallery';
import FAQ from './components/sections/FAQ';
import Footer from './components/layout/Footer';
import CookieBanner from './components/shared/CookieBanner';
import StructuredData from './components/shared/StructuredData';
import ShoppingCart from './components/pages/ShoppingCart';

// Loading component
const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-dark-primary">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto mb-4"></div>
      <p className="text-cream-dark font-light">Loading TamerinTECH AI Art...</p>
    </div>
  </div>
);

const App: React.FC = () => {
  const [currentView, setCurrentView] = useState('home');

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '') || 'home';
      // Only change view for specific routes, not for anchor links to sections
      if (hash === 'cart' || hash === 'home') {
        setCurrentView(hash);
      }
    };

    // Set initial view based on current hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Show cart view
  if (currentView === 'cart') {
    return (
      <CartProvider>
        <Suspense fallback={<LoadingSpinner />}>
          <div className="App min-h-screen bg-dark-primary">
            <StructuredData />
            <Header />
            <ShoppingCart />
            <CookieBanner />
          </div>
        </Suspense>
      </CartProvider>
    );
  }

  return (
    <CartProvider>
      <Suspense fallback={<LoadingSpinner />}>
        <div className="App min-h-screen bg-dark-primary">
        {/* SEO and Structured Data */}
        <StructuredData />
        
        {/* Header */}
        <Header />

        {/* Main Content */}
        <main>
          {/* Image Preview Gallery - 3 images before hero for immediate visual impact */}
          <ImagePreviewGallery />

          {/* Hero Section */}
          <Hero />

          {/* Contact Form - prominent placement for accessibility */}
          <ContactForm />

          {/* Pricing Section - standalone price list */}
          <Pricing />

          {/* Full Gallery Section */}
          <FullGallery />

          {/* FAQ Section - AEO optimized for search visibility */}
          <FAQ />

          {/* How We Work Section - moved to bottom before footer */}
          <HowWeWork />
        </main>

        {/* Footer */}
        <Footer />

        {/* Cookie Banner */}
        <CookieBanner />

        {/* Scroll to top button */}
        <motion.button
          className="fixed bottom-8 right-8 p-3 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-colors z-40 no-print"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          aria-label="Scroll to top"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </motion.button>
      </div>
    </Suspense>
    </CartProvider>
  );
};

export default App;